import { createStore, useStore } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

/**
 * Session state for the support chat, shared between the provider (which owns
 * the vendor callbacks) and the launcher bubble (which renders far below it,
 * under `<Outlet />`, so no prop can reach it).
 *
 * Deliberately NOT persisted: both fields describe the live messenger session
 * and must start false/0 on every load.
 */
interface SupportChatState {
  /** A session is booted and not shut down — the only time the bubble may show. */
  isReady: boolean;
  unreadCount: number;
  actions: {
    setReady: (ready: boolean) => void;
    setUnreadCount: (unreadCount: number) => void;
  };
}

const store = createStore<SupportChatState>()(
  devtools(
    immer((set) => ({
      isReady: false,
      unreadCount: 0,
      actions: {
        setReady: (ready) =>
          set(
            (state) => {
              state.isReady = ready;
            },
            false,
            { type: "setReady", ready },
          ),
        setUnreadCount: (unreadCount) =>
          set(
            (state) => {
              state.unreadCount = unreadCount;
            },
            false,
            { type: "setUnreadCount", unreadCount },
          ),
      },
    })),
    { name: "SupportChatStore" },
  ),
);

/**
 * Selector-based on purpose: the bubble subscribes to `isReady` and
 * `unreadCount` separately, so an unread tick never re-renders anything that
 * only cares whether a session exists.
 */
export const useSupportChatState = <T>(
  selector: (state: SupportChatState) => T,
): T => useStore(store, selector);

/**
 * Wired to `IntercomProvider`'s `onUnreadCountChange`.
 *
 * Module-level on purpose. The vendor registers this callback INSIDE its first
 * boot and never re-registers it, so it must be stable and correct from the
 * first render. A `useState` setter in the route component would also re-render
 * every member page on every unread tick.
 */
export const handleSupportChatUnreadCount = (unreadCount: number) =>
  store.getState().actions.setUnreadCount(unreadCount);

export const setSupportChatReady = (ready: boolean) =>
  store.getState().actions.setReady(ready);

export const resetSupportChatUnreadCount = () =>
  store.getState().actions.setUnreadCount(0);
